// InventoryManagment.jsx
import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { Search, Plus, RefreshCcw, Eye, Pencil, Image as ImageIcon, Barcode } from "lucide-react";
import "./InventoryAccordion.css";
import MyContext from "../../../MyContext";
import Swal from "sweetalert2";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CreateOrderModal from "./CreateOrderModal";
import CatalogueItemModal from "../../Order/Catalogue Components/CatalogueItemModal";

// ✅ import barcode modal
import AddBarcodeModal from "./AddBarcodeToitem";

const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";

// ---------------- helpers ----------------
const safeParse = (v, fallback = null) => {
  try {
    if (typeof v !== "string") return v ?? fallback;
    const t = v.trim();
    if (!t) return fallback;
    return JSON.parse(t);
  } catch {
    return fallback;
  }
};

const firstIconUrl = (item) => {
  const iconArr = safeParse(item?.icon_dish, null);
  if (Array.isArray(iconArr) && iconArr.length) return iconArr[0];
  if (typeof item?.icon_dish === "string" && item.icon_dish.startsWith("http")) return item.icon_dish;
  if (typeof item?.iconDish === "string" && item.iconDish.startsWith("http")) return item.iconDish;
  return "";
};

const dishLabelFrom = (item) => {
  const icon = firstIconUrl(item);
  const s = String(icon || "").toLowerCase();
  if (s.includes("veg")) return "Veg";
  if (s.includes("non")) return "Non-Veg";
  if (s.includes("egg")) return "Egg";

  const dishArr = safeParse(item?.dish, null);
  const d = Array.isArray(dishArr) ? String(dishArr[0]) : String(item?.dish || "");
  if (d === "1" || /veg/i.test(d)) return "Veg";
  if (d === "2" || /non/i.test(d)) return "Non-Veg";
  if (d === "3" || /egg/i.test(d)) return "Egg";
  return "";
};

const isProbablyJsonString = (val) => {
  if (typeof val !== "string") return false;
  const t = val.trim();
  return (t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"));
};

const formatPrimitive = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return String(v);
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return "—";
    if (t === "Y") return "Yes (Y)";
    if (t === "N") return "No (N)";
    return t;
  }
  return String(v);
};

const prettifyKey = (k) => {
  if (!k) return "";
  return String(k)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const shouldHideKey = (key) => {
  const k = String(key || "").toLowerCase();
  return ["photo"].includes(k);
};

const normalizeDetailEntries = (rawObj) => {
  if (!rawObj || typeof rawObj !== "object") return [];

  const priority = [
    "item_id",
    "id",
    "item_name",
    "status",
    "inStock",
    "price",
    "discount",
    "discount_price",
    "category_name",
    "sub_category",
    "size",
    "size_id",
    "item_category",
    "stock",
    "home_delivery",
    "takeaway",
    "dining",
    "barcodeNumber",
    "customizations",
  ];

  const keys = Object.keys(rawObj);

  const ordered = [...priority.filter((p) => keys.includes(p)), ...keys.filter((k) => !priority.includes(k))].filter(
    (k) => !shouldHideKey(k)
  );

  return ordered.map((k) => [k, rawObj[k]]);
};

// ---------------- small UI blocks ----------------
const Loader = () => (
  <div className="inv-loading-wrap">
    <div className="inv-loading-card">
      <div className="inv-spinner" />
      <div className="inv-loading-text">Loading inventory…</div>
    </div>
  </div>
);

const Badge = ({ tone = "slate", children }) => {
  return <span className={`inv-badge-industrial ${tone}`}>{children}</span>;
};

// ---------------- Details Modal (dynamic) ----------------
const InventoryDetailsModal = ({ open, onClose, item }) => {
  if (!open || !item) return null;

  const iconUrl = firstIconUrl(item);
  const dishLabel = dishLabelFrom(item);

  const entries = normalizeDetailEntries(item);

  return (
    <Modal show={open} onHide={onClose} centered size="lg" backdrop="static" className="inv-industrial-modal">
      <Modal.Header closeButton className="inv-industrial-modal-head">
        <Modal.Title className="inv-industrial-modal-title">
          <span className="inv-modal-title-main">{item?.item_name || "Item Details"}</span>
          <span className="inv-modal-title-sub">/api/Inventory/v1.0/getInventoryItems</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="inv-industrial-modal-body">
        <div className="inv-modal-top">
          <div className="inv-modal-photo">
            {item?.photo ? (
              <img src={item.photo} alt={item?.item_name || "item"} />
            ) : (
              <div className="inv-modal-photo-ph">
                <ImageIcon size={18} />
                <span>No image</span>
              </div>
            )}
          </div>

          <div className="inv-modal-kpis">
            <div className="inv-kpi">
              <div className="inv-kpi-label">Status</div>
              <div className="inv-kpi-value">
                {String(item?.inStock || item?.status || "—") === "Y" ? (
                  <Badge tone="green">Active</Badge>
                ) : (
                  <Badge tone="red">Inactive</Badge>
                )}
              </div>
            </div>

            <div className="inv-kpi">
              <div className="inv-kpi-label">Category</div>
              <div className="inv-kpi-value">{item?.category_name || "—"}</div>
            </div>

            <div className="inv-kpi">
              <div className="inv-kpi-label">Price</div>
              <div className="inv-kpi-value">₹{item?.price ?? "0"}</div>
            </div>

            <div className="inv-kpi">
              <div className="inv-kpi-label">Dish</div>
              <div className="inv-kpi-value">{dishLabel ? <Badge tone="slate">{dishLabel}</Badge> : "—"}</div>
            </div>

            <div className="inv-kpi">
              <div className="inv-kpi-label">Icon Dish</div>
              <div className="inv-kpi-value">
                {iconUrl ? (
                  <span className="inv-icon-pill">
                    <img src={iconUrl} alt="icon_dish" />
                    <span className="inv-icon-pill-text">{dishLabel || "Icon"}</span>
                  </span>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="inv-modal-grid">
          {entries.map(([k, v]) => {
            const keyLabel = prettifyKey(k);

            let value = v;
            if (isProbablyJsonString(v)) {
              const parsed = safeParse(v, null);
              if (parsed !== null) value = parsed;
            }

            if (Array.isArray(value)) {
              const allPrimitive = value.every((x) => x === null || ["string", "number", "boolean"].includes(typeof x));
              if (allPrimitive) {
                return (
                  <div className="inv-modal-field" key={k}>
                    <div className="inv-modal-field-k">{keyLabel}</div>
                    <div className="inv-modal-field-v">
                      <div className="inv-chip-wrap">
                        {value.length ? (
                          value.map((x, idx) => (
                            <span className="inv-chip" key={idx}>
                              {formatPrimitive(x)}
                            </span>
                          ))
                        ) : (
                          <span className="inv-dash">—</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="inv-modal-field inv-span-2" key={k}>
                  <div className="inv-modal-field-k">{keyLabel}</div>
                  <div className="inv-modal-field-v">
                    <pre className="inv-json">{JSON.stringify(value, null, 2)}</pre>
                  </div>
                </div>
              );
            }

            if (value && typeof value === "object") {
              return (
                <div className="inv-modal-field inv-span-2" key={k}>
                  <div className="inv-modal-field-k">{keyLabel}</div>
                  <div className="inv-modal-field-v">
                    <pre className="inv-json">{JSON.stringify(value, null, 2)}</pre>
                  </div>
                </div>
              );
            }

            const s = String(value ?? "");
            const looksUrl = s.startsWith("http://") || s.startsWith("https://");

            return (
              <div className="inv-modal-field" key={k}>
                <div className="inv-modal-field-k">{keyLabel}</div>
                <div className="inv-modal-field-v">
                  {looksUrl ? (
                    <a className="inv-link" href={s} target="_blank" rel="noreferrer">
                      {s}
                    </a>
                  ) : (
                    <span>{formatPrimitive(value)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer className="inv-industrial-modal-foot">
        <button className="inv-btn inv-btn-ghost" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

// ---------------- MAIN ----------------
const InventoryAccordion = ({ userID }) => {
  const { baseUrl, config } = useContext(MyContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [inventoryRaw, setInventoryRaw] = useState([]);

  const [q, setQ] = useState("");

  const [barcodeValue, setBarcodeValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const [invCategories, setInvCategories] = useState([]);
  const [invSubCategories, setInvSubCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [viewModalItem, setViewModalItem] = useState(null);
  const [viewModalTab, setViewModalTab] = useState("details");
  const [editableDetails, setEditableDetails] = useState({});
  const [isEditingDetails, setIsEditingDetails] = useState(false);

  // ✅ barcode modal state
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [barcodeModalItem, setBarcodeModalItem] = useState(null);

  const openBarcodeModal = (it) => {
    setBarcodeModalItem(it);
    setBarcodeModalOpen(true);
  };

  // stubs for CatalogueItemModal (availability/printer) – safe defaults
  const [availabilityData] = useState({});
  const handleCheckboxChange = useCallback(() => {}, []);
  const handleSubmit = useCallback((e) => e?.preventDefault?.(), []);
  const [printerList] = useState([]);
  const [printerLoading] = useState(false);
  const fetchPrinterList = useCallback(() => {}, []);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const setAddPrinterModalOpen = useCallback(() => {}, []);
  const handleSavePrinterToItem = useCallback(() => {}, []);

  const handleNumberWheel = useCallback((e) => e?.target?.blur?.(), []);
  const [variants] = useState([]);
  const [loadingVariants] = useState(false);

  // ---------- API: getInventoryItems ----------
  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getInventoryItems`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${BASIC_AUTH}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userid: userID, orderType: "ALL" }),
      });
      const data = await res.json();

      if (data?.responseCode === "00") {
        setInventoryRaw(Array.isArray(data.response) ? data.response : []);
      } else {
        Swal.fire("Error", data?.responseMessage || "Failed to load inventory", "error");
        setInventoryRaw([]);
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load inventory", "error");
      setInventoryRaw([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, userID]);

  // ---------- API: categories / subcategories (for add modal) ----------
  const fetchInventoryCategories = async () => {
    try {
      const res = await axios.post(`${baseUrl}/api/Inventory/v1.0/getInventoryCategories`, { userid: userID }, config);
      if (res.data?.responseCode === "00") {
        const list = (res.data.response || []).map((r) => ({
          label: r.category,
          value: r.category,
          catId: r.catId || "",
        }));
        setInvCategories(list);
        return list;
      }
    } catch (err) {
      console.error("getInventoryCategories error:", err);
    }
    setInvCategories([]);
    return [];
  };

  const fetchInventorySubCategories = async (category) => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getInventorySubCategories`,
        { userid: userID, category },
        config
      );
      if (res.data?.responseCode === "00") {
        const list = (res.data.response || []).map((r) => ({
          label: r.subCategory || r.sub_category || r.category,
          value: r.subCategory || r.sub_category || r.category,
        }));
        setInvSubCategories(list);
        return list;
      }
    } catch (err) {
      console.error("getInventorySubCategories error:", err);
    }
    setInvSubCategories([]);
    return [];
  };

  useEffect(() => {
    (async () => {
      const cats = await fetchInventoryCategories();
      if (cats.length) await fetchInventorySubCategories(cats[0].value);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, userID]);

  // ---------- search filter ----------
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return inventoryRaw;

    return inventoryRaw.filter((it) => {
      const name = String(it?.item_name || "").toLowerCase();
      const code = String(it?.item_id || it?.id || "").toLowerCase();
      const cat = String(it?.category_name || "").toLowerCase();
      const dish = String(dishLabelFrom(it) || "").toLowerCase();
      return name.includes(needle) || code.includes(needle) || cat.includes(needle) || dish.includes(needle);
    });
  }, [inventoryRaw, q]);

  // ---------- quick item details fetch (for edit modal prep) ----------
  const fetchInventoryItemDetails = async (itemId) => {
    if (!itemId) return null;
    const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getInventoryItemDetails`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Basic ${BASIC_AUTH}`,
      },
      body: JSON.stringify({ userid: userID, itemId: String(itemId) }),
    });
    const data = await res.json();
    if (data?.responseCode === "00" && data?.response) return data.response;
    return null;
  };

  const openDetails = (row) => {
    setDetailItem(row);
    setDetailOpen(true);
  };

  const openEdit = async (row) => {
    try {
      setIsSearching(true);

      const fresh = await fetchInventoryItemDetails(row?.item_id || row?.id);
      const itemForModal = fresh || row;

      setViewModalTab("details");
      setViewModalItem(itemForModal);

      setEditableDetails({
        ...itemForModal,
        item_id: itemForModal?.item_id || itemForModal?.itemId || "",
        item_name: itemForModal?.item_name || "",
        photo: itemForModal?.photo || "",
        rackNo: itemForModal?.rackNumber || itemForModal?.rack_no || "",
        expiryDate: itemForModal?.expiryDate || "",
      });

      setIsEditingDetails(false);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to open edit modal", "error");
    } finally {
      setIsSearching(false);
    }
  };

  const onBarcodeKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    const code = barcodeValue.trim();
    if (!code) return;

    try {
      setIsSearching(true);
      const fresh = await fetchInventoryItemDetails(code);
      if (!fresh) {
        Swal.fire("Not Found", "No item found for this ID / barcode", "warning");
        return;
      }
      await openEdit(fresh);
    } finally {
      setIsSearching(false);
    }
  };

  const statusTone = (it) => (String(it?.inStock || it?.status || "N") === "Y" ? "green" : "red");

  return (
    <div className="inv-industrial-wrap">
      {/* Header */}
      <div className="inv-industrial-head">
        <div className="inv-industrial-title">
          <h2 className="CreateCampaign">Inventory Management</h2>
        </div>

        <div className="inv-industrial-actions">
          {/* Barcode / ItemID search */}
          <div className={`inv-industrial-search ${isSearching ? "is-loading" : ""}`}>
            <Search size={16} />
            <input
              placeholder="Scan / enter Barcode or Item ID"
              value={barcodeValue}
              onChange={(e) => setBarcodeValue(e.target.value)}
              onKeyDown={onBarcodeKeyDown}
            />
            {isSearching && <RefreshCcw className="spin ms-2" size={16} />}
          </div>

          {/* Global search */}
          <div className="inv-industrial-search">
            <Search size={16} />
            <input
              placeholder="Search name / item_id / category / dish…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button type="button" className="inv-btn inv-btn-primary" onClick={() => navigate("/Orders/Catalogue/add-items")}>
            <Plus size={16} style={{ marginRight: 6 }} />
            Add Item
          </button>

          <button className="inv-btn inv-btn-ghost" onClick={fetchInventory} title="Refresh">
            <RefreshCcw size={16} />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <div className="inv-industrial-card">
          <div className="inv-industrial-table">
            <div className="inv-industrial-row inv-industrial-headrow">
              <div>Image</div>
              <div>Name</div>
              <div>Status</div>
              <div>Price</div>
              <div>Icon Dish</div>
              <div>Category</div>
              <div className="inv-industrial-right">Actions</div>
            </div>

            {filtered.length === 0 ? (
              <div className="inv-industrial-empty">No items found.</div>
            ) : (
              filtered.map((it) => {
                const iconUrl = firstIconUrl(it);
                const dishLabel = dishLabelFrom(it);

                const hasBarcode = Boolean(String(it?.barcodeNumber || "").trim());

                return (
                  <div className="inv-industrial-row" key={String(it?.id ?? it?.item_id ?? Math.random())}>
                    {/* Image */}
                    <div className="inv-industrial-imgcell">
                      {it?.photo ? (
                        <img src={it.photo} alt={it?.item_name || "item"} className="inv-industrial-thumb" />
                      ) : (
                        <div className="inv-industrial-thumb ph">
                          <ImageIcon size={16} />
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <div className="inv-industrial-name">
                      <div className="inv-industrial-name-main">{it?.item_name || "—"}</div>
                      <div className="inv-industrial-name-sub">
                        <span className="inv-mono">item_id:</span> {it?.item_id || "—"}
                        <br />
                        <span className="inv-mono">Barcode:</span>{" "}
                        {hasBarcode ? (
                          it?.barcodeNumber
                        ) : (
                          <span
                            className="inv-link"
                            style={{ cursor: "pointer" }}
                            onClick={() => openBarcodeModal(it)}
                            title="Add barcode"
                          >
                            Add barcode
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <Badge tone={statusTone(it)}>
                        {String(it?.inStock || it?.status || "N") === "Y" ? "Active" : "Inactive"}
                      </Badge>
                    </div>

                    {/* Price */}
                    <div className="inv-industrial-price">₹{it?.price ?? "0"}</div>

                    {/* Icon dish */}
                    <div>
                      {iconUrl ? (
                        <span className="inv-icon-pill">
                          <img src={iconUrl} alt="icon_dish" />
                          <span className="inv-icon-pill-text">{dishLabel || "Dish"}</span>
                        </span>
                      ) : (
                        <span className="inv-dash">—</span>
                      )}
                    </div>

                    {/* Category */}
                    <div className="inv-industrial-cat">{it?.category_name || "—"}</div>

                    {/* Actions */}
                    <div className="inv-industrial-actions-cell">
                      <button className="inv-iconbtn" onClick={() => openDetails(it)} title="Details">
                        <Eye size={16} />
                      </button>

                      <button className="inv-iconbtn primary" onClick={() => openEdit(it)} title="Edit">
                        <Pencil size={16} />
                      </button>

                      {/* ✅ NEW: Barcode action */}
                      <button className="inv-iconbtn" onClick={() => openBarcodeModal(it)} title="Barcode">
                        <Barcode size={16} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* DETAILS MODAL */}
      <InventoryDetailsModal
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailItem(null);
        }}
        item={detailItem}
      />

      {/* ADD ITEM MODAL (kept as-is) */}
      <CreateOrderModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        userID={userID}
        BASIC_AUTH={BASIC_AUTH}
        baseUrl={baseUrl}
        categories={invCategories}
        subcategories={invSubCategories}
        initialCategory={invCategories[0]?.value || ""}
        loadSubcategories={fetchInventorySubCategories}
        mode="add"
        onSaved={() => {
          setShowAddModal(false);
          fetchInventory();
        }}
      />

      {/* EDIT ITEM MODAL (Catalogue flow) */}
      <CatalogueItemModal
        viewModalItem={viewModalItem}
        setViewModalItem={(v) => {
          setViewModalItem(v);
          if (!v) {
            setIsEditingDetails(false);
            setEditableDetails({});
          }
        }}
        viewModalTab={viewModalTab}
        setViewModalTab={setViewModalTab}
        editableDetails={editableDetails}
        setEditableDetails={setEditableDetails}
        isEditingDetails={isEditingDetails}
        setIsEditingDetails={setIsEditingDetails}
        handleNumberWheel={handleNumberWheel}
        availabilityData={availabilityData}
        handleCheckboxChange={handleCheckboxChange}
        handleSubmit={handleSubmit}
        printerList={printerList}
        printerLoading={printerLoading}
        fetchPrinterList={fetchPrinterList}
        selectedPrinter={selectedPrinter}
        setSelectedPrinter={setSelectedPrinter}
        setAddPrinterModalOpen={setAddPrinterModalOpen}
        handleSavePrinterToItem={handleSavePrinterToItem}
        navigate={navigate}
        userID={userID}
        baseUrl={baseUrl}
        config={config}
        onItemUpdated={() => {
          fetchInventory();
        }}
        variants={variants}
        loadingVariants={loadingVariants}
      />

      {/* ✅ BARCODE MODAL */}
      {barcodeModalOpen && barcodeModalItem ? (
        <AddBarcodeModal
          item={barcodeModalItem}
          userID={userID}
          baseUrl={baseUrl}
          config={config}
          onSaved={() => {
            // refresh list after barcode saved
            fetchInventory();
          }}
          onClose={() => {
            setBarcodeModalOpen(false);
            setBarcodeModalItem(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default InventoryAccordion;
